import React, { useMemo } from 'react';
import { PowerDataResponse } from '../../../services/powerService';

interface PowerDataSummaryProps {
  powerData: PowerDataResponse;
}

const PowerDataSummary: React.FC<PowerDataSummaryProps> = ({ powerData }) => {
  // Calculate key metrics from data
  const metrics = useMemo(() => {
    if (!powerData || powerData.data.length === 0) {
      return null;
    }

    // Find the latest data point with non-zero load specifically
    let latestDataPoint = null;
    let latestDataPointIndex = powerData.data.length - 1;

    // Search backwards to find the latest data point with non-zero load
    while (latestDataPointIndex >= 0) {
      const dp = powerData.data[latestDataPointIndex];
      if ((dp.load || 0) > 0) {
        latestDataPoint = dp;
        break;
      }
      latestDataPointIndex--;
    }

    // If no point with load > 0 was found, find one with any generation
    if (!latestDataPoint) {
      latestDataPointIndex = powerData.data.length - 1;
      while (latestDataPointIndex >= 0) {
        const dp = powerData.data[latestDataPointIndex];
        const hasGeneration =
          (dp.solar || 0) > 0 ||
          (dp.wind_onshore || 0) > 0 ||
          (dp.wind_offshore || 0) > 0 ||
          (dp.biomass || 0) > 0 ||
          (dp.nuclear || 0) > 0 ||
          (dp.fossil_gas || 0) > 0 ||
          (dp.fossil_brown_coal || 0) > 0 ||
          (dp.fossil_hard_coal || 0) > 0;

        if (hasGeneration) {
          latestDataPoint = dp;
          break;
        }
        latestDataPointIndex--;
      }
    }

    // If still no valid point found, use the last data point as fallback
    if (!latestDataPoint) {
      latestDataPoint = powerData.data[powerData.data.length - 1];
    }

    // Make sure we have a data point to work with
    if (!latestDataPoint) {
      return null;
    }

    // Calculate renewable generation
    const renewableGeneration =
      (latestDataPoint.solar || 0) +
      (latestDataPoint.wind_onshore || 0) +
      (latestDataPoint.wind_offshore || 0) +
      (latestDataPoint.biomass || 0) +
      (latestDataPoint.geothermal || 0) +
      (latestDataPoint.hydro_run_of_river || 0) +
      (latestDataPoint.hydro_water_reservoir || 0) +
      (latestDataPoint.other_renewables || 0);

    // Calculate conventional generation
    const conventionalGeneration =
      (latestDataPoint.nuclear || 0) +
      (latestDataPoint.fossil_brown_coal || 0) +
      (latestDataPoint.fossil_hard_coal || 0) +
      (latestDataPoint.fossil_gas || 0) +
      (latestDataPoint.fossil_oil || 0) +
      (latestDataPoint.waste || 0) +
      (latestDataPoint.others || 0);

    // Calculate total generation
    const totalGeneration = renewableGeneration + conventionalGeneration;

    // Calculate renewable percentage
    const renewablePercentage =
      totalGeneration > 0 ? (renewableGeneration / totalGeneration) * 100 : 0;

    // Calculate average metrics over the entire period
    let totalRenewable = 0;
    let totalConventional = 0;
    let totalLoad = 0;
    let validDataPoints = 0;

    powerData.data.forEach((dataPoint) => {
      const renewable =
        (dataPoint.solar || 0) +
        (dataPoint.wind_onshore || 0) +
        (dataPoint.wind_offshore || 0) +
        (dataPoint.biomass || 0) +
        (dataPoint.geothermal || 0) +
        (dataPoint.hydro_run_of_river || 0) +
        (dataPoint.hydro_water_reservoir || 0) +
        (dataPoint.other_renewables || 0);

      const conventional =
        (dataPoint.nuclear || 0) +
        (dataPoint.fossil_brown_coal || 0) +
        (dataPoint.fossil_hard_coal || 0) +
        (dataPoint.fossil_gas || 0) +
        (dataPoint.fossil_oil || 0) +
        (dataPoint.waste || 0) +
        (dataPoint.others || 0);

      if (renewable || conventional || dataPoint.load) {
        totalRenewable += renewable;
        totalConventional += conventional;
        totalLoad += dataPoint.load || 0;
        validDataPoints++;
      }
    });

    const avgRenewable = validDataPoints > 0 ? totalRenewable / validDataPoints : 0;
    const avgConventional = validDataPoints > 0 ? totalConventional / validDataPoints : 0;
    const avgLoad = validDataPoints > 0 ? totalLoad / validDataPoints : 0;
    const avgTotalGeneration = avgRenewable + avgConventional;
    const avgRenewablePercentage =
      avgTotalGeneration > 0 ? (avgRenewable / avgTotalGeneration) * 100 : 0;

    // Find peak load and generation
    let peakLoad = 0;
    let peakGeneration = 0;
    let peakRenewable = 0;

    powerData.data.forEach((dataPoint) => {
      const load = dataPoint.load || 0;
      if (load > peakLoad) {
        peakLoad = load;
      }

      const renewable =
        (dataPoint.solar || 0) +
        (dataPoint.wind_onshore || 0) +
        (dataPoint.wind_offshore || 0) +
        (dataPoint.biomass || 0) +
        (dataPoint.geothermal || 0) +
        (dataPoint.hydro_run_of_river || 0) +
        (dataPoint.hydro_water_reservoir || 0) +
        (dataPoint.other_renewables || 0);

      const conventional =
        (dataPoint.nuclear || 0) +
        (dataPoint.fossil_brown_coal || 0) +
        (dataPoint.fossil_hard_coal || 0) +
        (dataPoint.fossil_gas || 0) +
        (dataPoint.fossil_oil || 0) +
        (dataPoint.waste || 0) +
        (dataPoint.others || 0);

      const totalGen = renewable + conventional;

      if (totalGen > peakGeneration) {
        peakGeneration = totalGen;
      }

      if (renewable > peakRenewable) {
        peakRenewable = renewable;
      }
    });

    return {
      country: powerData.country_code,
      currentLoad: latestDataPoint.load || 0,
      currentRenewable: renewableGeneration,
      currentConventional: conventionalGeneration,
      currentTotal: totalGeneration,
      renewablePercentage,
      avgRenewable,
      avgConventional,
      avgLoad,
      avgRenewablePercentage,
      peakLoad,
      peakGeneration,
      peakRenewable,
      dataPointCount: validDataPoints,
    };
  }, [powerData]);

  if (!metrics) {
    return <div>No data available</div>;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Power Summary - {metrics.country}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Current Generation Mix Card */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Current Generation Mix
          </h4>
          <div className="mt-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">Renewable</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.currentRenewable).toLocaleString()} MW
              </span>
            </div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">Conventional</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.currentConventional).toLocaleString()} MW
              </span>
            </div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">Total Generation</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.currentTotal).toLocaleString()} MW
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">Current Load</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.currentLoad).toLocaleString()} MW
              </span>
            </div>
          </div>
        </div>

        {/* Renewable Percentage Card */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Renewable Energy Share
          </h4>
          <div className="flex items-center mt-2">
            <div className="relative w-full h-4 bg-gray-200 dark:bg-gray-600 rounded-full">
              <div
                className="absolute top-0 left-0 h-4 bg-gradient-to-r from-green-400 to-green-600 rounded-full"
                style={{ width: `${Math.min(metrics.renewablePercentage, 100)}%` }}
              ></div>
            </div>
            <span className="ml-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {Math.round(metrics.renewablePercentage)}%
            </span>
          </div>
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Period average: {Math.round(metrics.avgRenewablePercentage)}%
          </div>
        </div>

        {/* Average Generation Card */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Period Averages</h4>
          <div className="mt-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">Avg. Renewable</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.avgRenewable).toLocaleString()} MW
              </span>
            </div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">Avg. Conventional</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.avgConventional).toLocaleString()} MW
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">Avg. Load</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.avgLoad).toLocaleString()} MW
              </span>
            </div>
          </div>
        </div>

        {/* Peak Values Card */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Peak Values</h4>
          <div className="mt-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">Peak Load</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.peakLoad).toLocaleString()} MW
              </span>
            </div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">Peak Generation</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.peakGeneration).toLocaleString()} MW
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">Peak Renewable</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {Math.round(metrics.peakRenewable).toLocaleString()} MW
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerDataSummary;
